import React, { useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Upload.css';

const POLL_INTERVAL_MS = 3000;
const DEFAULT_TARGET_LANG = 'zh';
const DEFAULT_CONTENT_COL = 'para_content';
const DEFAULT_PROJECT_NICKNAME = 'extravagant_check';

const createTaskUuid = () => {
  const generatedUuid = typeof uuidv4 === 'function' ? uuidv4() : '';
  if (generatedUuid) {
    return generatedUuid;
  }

  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `task-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getTaskProgress = (task) => {
  const progress = Number.parseInt(task?.progress, 10);
  return Number.isNaN(progress) ? 0 : progress;
};

const getTaskErrorMessage = (task) => {
  if (!task) return '';
  return task.runtime_error || task.error_message || task.message || '';
};

const getDownloadFileName = (contentDisposition) => {
  if (!contentDisposition) return 'result.xlsx';

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1] || 'result.xlsx';
};

const RegulationsUpload = () => {
  const navigate = useNavigate();
  const [uploadFile, setUploadFile] = useState(null);
  const [projectNickName, setProjectNickName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [activeTaskUuid, setActiveTaskUuid] = useState('');
  const [taskProgress, setTaskProgress] = useState(null);
  const [taskType, setTaskType] = useState('extravagant_check');
  const [taskError, setTaskError] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');
  const [isPolling, setIsPolling] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const taskCompleted = useMemo(() => taskProgress === 100 && !taskError, [taskError, taskProgress]);

  const resetTaskState = () => {
    setActiveTaskUuid('');
    setTaskProgress(null);
    setTaskType('extravagant_check');
    setTaskError('');
    setUploadMessage('');
    setIsPolling(false);
    setIsDownloading(false);
  };

  const onDrop = (acceptedFiles) => {
    setUploadFile(acceptedFiles[0] || null);
    resetTaskState();
  };

  const onDropRejected = () => {
    toast.error('The file is too large. Please upload a file smaller than 50MB.');
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxSize: 50 * 1024 * 1024,
    multiple: false,
  });

  const fetchTaskStatus = async (uuid) => {
    const response = await fetch('/api/v1/tasks', {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch task progress.');
    }

    const data = await response.json().catch(() => ({}));
    const task = Array.isArray(data?.data) ? data.data.find((item) => item.uuid === uuid) : null;

    if (!task) {
      return null;
    }

    const progress = getTaskProgress(task);
    const errorMessage = getTaskErrorMessage(task);

    setTaskProgress(progress);
    setTaskType(task.task_type || 'extravagant_check');

    if (errorMessage) {
      setTaskError(errorMessage);
      setIsPolling(false);
      return task;
    }

    if (progress >= 100) {
      setIsPolling(false);
      setTaskProgress(100);
    }

    return task;
  };

  useEffect(() => {
    if (!activeTaskUuid || !isPolling) {
      return undefined;
    }

    let isMounted = true;
    let timeoutId;

    const poll = async () => {
      try {
        const task = await fetchTaskStatus(activeTaskUuid);
        if (!isMounted) return;

        if (task && (getTaskErrorMessage(task) || getTaskProgress(task) >= 100)) {
          return;
        }
      } catch (error) {
        if (!isMounted) return;
        setTaskError(error.message || 'Failed to fetch task progress.');
        setIsPolling(false);
        return;
      }

      timeoutId = window.setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      isMounted = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [activeTaskUuid, isPolling]);

  const handleClickStart = async () => {
    if (!uploadFile) {
      toast.error('Please upload a PDF file first.');
      return;
    }

    const uuid = createTaskUuid();
    const normalizedProjectName = projectNickName.trim() || DEFAULT_PROJECT_NICKNAME;
    const formData = new FormData();
    formData.append('uuid', uuid);
    formData.append('file', uploadFile);
    formData.append('target_lang', DEFAULT_TARGET_LANG);
    formData.append('content_col', DEFAULT_CONTENT_COL);
    formData.append('project_nickname', normalizedProjectName);

    setIsUploading(true);
    setTaskError('');
    setUploadMessage('');
    setTaskProgress(0);
    setTaskType('extravagant_check');
    setActiveTaskUuid(uuid);
    setIsPolling(false);

    try {
      const response = await fetch('/api/v1/extravagant/upload', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.message || 'Upload failed.');
      }

      setUploadMessage(data?.message || 'Task submitted successfully.');
      setIsPolling(true);
      fetchTaskStatus(uuid).catch((pollError) => {
        setTaskError(pollError.message || 'Failed to fetch task progress.');
        setIsPolling(false);
      });
    } catch (error) {
      setTaskError(error.message || 'Upload failed.');
      setTaskProgress(null);
      setActiveTaskUuid('');
      toast.error(error.message || 'Upload failed.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async () => {
    if (!activeTaskUuid) return;

    setIsDownloading(true);
    try {
      const response = await fetch(`/api/v1/result?uuid=${encodeURIComponent(activeTaskUuid)}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || 'Download failed.');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getDownloadFileName(response.headers.get('content-disposition'));
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message || 'Download failed.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleUploadAnother = () => {
    setUploadFile(null);
    setProjectNickName('');
    resetTaskState();
  };

  return (
    <div className="upload-container">
      <ToastContainer />
      {isUploading ? (
        <div className="uploading-animation">
          <div className="spinner"></div>
          <p>Uploading your PDF and creating the task...</p>
        </div>
      ) : (
        <>
          <h2 className="upload-title">Regulations Check (Extravagant Style Check)</h2>
          <p>Please upload one PDF document for extravagant style checking.</p>
          <div className="dropzone-container single-dropzone">
            <div {...getRootProps({ className: `dropzone ${isDragActive ? 'dropzone-active' : ''} ${uploadFile ? 'dropzone-filled' : ''}` })}>
              <input {...getInputProps()} />
              {uploadFile ? (
                <p>{uploadFile.name}</p>
              ) : (
                <p>Drag and drop <strong>PDF</strong> here, or click to select</p>
              )}
            </div>
          </div>

          {uploadFile && (
            <div className="upload-btn-container">
              <p>
                Optionally, to help you better distinguish your tasks, you can give this task a name.
              </p>
              <input
                type="text"
                className="project-name-input"
                placeholder="Enter Project Name Here (e.g. Project Himalaya V1)"
                value={projectNickName}
                onChange={(e) => setProjectNickName(e.target.value)}
              />
              <button className="btn" onClick={handleClickStart} disabled={isPolling}>
                Start Regulations Check
              </button>
            </div>
          )}

          {(uploadMessage || activeTaskUuid || taskError !== '' || taskCompleted) && (
            <div className="task-status-card">
              <h3>Current Task Status</h3>
              {uploadMessage && <p className="task-status-message">{uploadMessage}</p>}
              {activeTaskUuid && (
                <div className="task-status-meta">
                  <span><strong>UUID:</strong> {activeTaskUuid}</span>
                  <span><strong>Task Type:</strong> {taskType}</span>
                </div>
              )}

              {taskError ? (
                <p className="task-status-error">{taskError}</p>
              ) : activeTaskUuid ? (
                <>
                  <div className="task-progress-bar-container" aria-label="Task Progress">
                    <div className="task-progress-bar" style={{ width: `${taskProgress || 0}%` }}>
                      {taskProgress || 0}%
                    </div>
                  </div>
                  <p className="task-status-hint">
                    {taskCompleted
                      ? 'The result file is ready.'
                      : isPolling
                        ? 'Polling the backend every few seconds for the latest progress.'
                        : 'Waiting for the backend to pick up the task.'}
                  </p>
                </>
              ) : null}

              <div className="task-status-actions">
                {taskCompleted && (
                  <button className="btn" onClick={handleDownload} disabled={isDownloading}>
                    {isDownloading ? 'Downloading...' : 'Download Result'}
                  </button>
                )}
                <button className="btn-secondary" onClick={() => navigate('/reports')}>
                  View All Tasks
                </button>
                <button className="btn-secondary" onClick={handleUploadAnother}>
                  Upload Another PDF
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RegulationsUpload;

